import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, requestErrorMessage } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Bell, Wallet, PackageX, CalendarClock, BadgeCheck, ArrowUpRight } from "lucide-react";

export default function Notifications() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError("");
      setWarnings([]);
      try {
        const [inventoryResult, expensesResult] = await Promise.all([
          http.get("/inventory/summary")
            .then((response) => ({ data: response.data }))
            .catch((requestError) => ({ error: requestError })),
          http.get("/expenses")
            .then((response) => ({ data: response.data }))
            .catch((requestError) => ({ error: requestError })),
        ]);
        if (!active) return;

        const nextWarnings = [];
        if (inventoryResult.error) nextWarnings.push(requestErrorMessage(inventoryResult.error, "load inventory alerts"));
        if (expensesResult.error) nextWarnings.push(requestErrorMessage(expensesResult.error, "load expense alerts"));
        const list = [];
        const inventoryRows = Array.isArray(inventoryResult.data?.rows) ? inventoryResult.data.rows : null;
        const expenses = Array.isArray(expensesResult.data) ? expensesResult.data : null;
        if (!inventoryResult.error && !inventoryRows) nextWarnings.push("Inventory alert data is unavailable.");
        if (!expensesResult.error && !expenses) nextWarnings.push("Expense alert data is unavailable.");
        if (inventoryRows) {
          const low = inventoryRows.filter((row) => row.low);
          if (low.length) list.push({ icon: PackageX, tone: "rose", title: "Low Stock", msg: `${low.length} item(s) at or below reorder level`, to: "/inventory" });
          const expiring = inventoryRows.filter((row) => {
            if (!row.expiry_date) return false; const date = new Date(row.expiry_date); const days = (date - new Date()) / 86400000; return days > 0 && days < 90;
          });
          if (expiring.length) list.push({ icon: CalendarClock, tone: "amber", title: "Expiry Alert", msg: `${expiring.length} batch(es) expiring within 90 days`, to: "/products" });
        }
        if (expenses) {
          const pending = expenses.filter((expense) => expense.status === "submitted");
          if (pending.length) list.push({ icon: BadgeCheck, tone: "brand", title: "Expense Approvals", msg: `${pending.length} claim(s) awaiting approval`, to: "/approvals" });
          const approved = expenses.filter((expense) => expense.status === "approved");
          if (approved.length) list.push({ icon: Wallet, tone: "emerald", title: "Ready to Pay", msg: `${approved.length} approved claim(s) awaiting payment`, to: "/approvals" });
        }
        setAlerts(list);
        setWarnings(nextWarnings);
      } catch (requestError) {
        if (active) setError(requestErrorMessage(requestError, "load notifications"));
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [retry]);

  const tones = { rose: "bg-rose-50 text-rose-600", amber: "bg-amber-50 text-amber-600", brand: "bg-brand-50 text-brand-700", emerald: "bg-emerald-50 text-emerald-600" };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div><h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Bell className="h-6 w-6 text-brand-600" /> Notifications</h1>
        <p className="text-sm text-muted-foreground">Live alerts across stock, expiry and approvals</p></div>
      {loading && <p className="text-sm text-muted-foreground" role="status">Loading notifications…</p>}
      {error && <div className="flex items-center gap-3 text-sm text-rose-700" role="alert"><span>{error}</span><button onClick={() => setRetry((value) => value + 1)} className="underline">Retry</button></div>}
      {warnings.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 text-sm text-amber-700" role="status">
          <span>Showing available alerts only. {warnings.join(" ")}</span>
          <button onClick={() => setRetry((value) => value + 1)} disabled={loading} className="underline disabled:opacity-50">Retry</button>
        </div>
      )}
      {!loading && !error && alerts.length === 0 && warnings.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground"><Bell className="h-8 w-8 mx-auto mb-2 opacity-40" />You're all caught up — no alerts right now</Card>
      ) : alerts.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {alerts.map((a, i) => (
            <Card key={i} className="p-4 flex items-center gap-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(a.to)} data-testid={`notif-${a.title.replace(/\s/g, "-").toLowerCase()}`}>
              <div className={`h-11 w-11 rounded-lg grid place-items-center ${tones[a.tone]}`}><a.icon className="h-5 w-5" /></div>
              <div className="flex-1 min-w-0"><div className="font-heading font-semibold">{a.title}</div><div className="text-sm text-muted-foreground">{a.msg}</div></div>
              <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
            </Card>
          ))}
        </div>
      ) : null}
    </div>
  );
}
